import { FormEvent, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import Input from "components/ui/input/Input";
import Label from "components/ui/input/Label";
import {
  betterAuthErrorText,
  signInWithEnterpriseSSO,
  signInWithProvider,
  type SocialProviderType,
} from "lib/auth/authClient";
import { passwordFieldRules } from "lib/auth/passwordRules";
import { useAwsWafWarmup } from "lib/auth/useAwsWafWarmup";
import { betterAuthSessionQueryOptions } from "lib/hooks/api/auth/betterAuthSessionQuery";
import {
  type AllowedLoginMethods,
  DEFAULT_LOGIN_METHODS,
  useMigrationStatus,
} from "lib/hooks/api/auth/useMigrationStatus";
import { useSignIn } from "lib/hooks/api/auth/useSignIn";
import Alert from "../../components/ui/alert/Alert";
import { Card, CollapsedField, Form, Page } from "./authPageStyles";
import { ExternalSignInButtons, trackSocialSignIn } from "./ExternalSignInButtons";
import { StotlesLogo } from "./StotlesLogo";
import { buildSignupContext } from "./tracking";
import { useLinkedProvider } from "./useLinkedProvider";
import { useProviderRedirect } from "./useProviderRedirect";

type FormValues = {
  email: string;
  password: string;
};

// The methods that hand the browser to an identity provider. Password isn't one: it needs a field
// filled in here, so it's never started on the user's behalf.
type RedirectSignInMethod = "sso" | SocialProviderType;

const REDIRECT_SIGN_IN_METHODS: RedirectSignInMethod[] = ["sso", "google", "microsoft"];

// The one redirect method a company allows, or null when it allows a password, several of them, or
// none at all. A chooser with one option in it has only one outcome, so the page takes it.
function soleRedirectMethod(methods: AllowedLoginMethods): RedirectSignInMethod | null {
  if (methods.password) return null;
  const available = REDIRECT_SIGN_IN_METHODS.filter((method) => methods[method]);
  return available.length === 1 ? available[0] : null;
}

/**
 * Identifier-first login. We ask for the email first because its domain decides how the user can
 * sign in.
 *
 * Both the email and password inputs stay mounted across steps (the inactive one is visually hidden)
 * so password managers can autofill username + password in a single pass. The step just controls
 * which is visible/interactive and which validation runs.
 *
 * The company's allowed methods decide what the second step offers. Enterprise SSO sits alongside
 * password and social rather than replacing them, so all three can appear together — unless one is
 * the only way in, in which case the email step starts it instead of rendering a button.
 */
export function BetterAuthLoginPage() {
  // Post-login destination, optional email pre-fill, and marketing attribution (for a Google /
  // Microsoft sign-in that creates a new user). Sanitised by the route's validateSearch.
  // `strict: false` lets it resolve to undefined when rendered outside its route (e.g. tests).
  const search = useSearch({ strict: false });
  const { redirect, email: prefillEmail, provider: linkedProvider } = search;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    clearErrors,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { email: prefillEmail ?? "", password: "" },
    // Validate on submit, and after a failed submit re-check on blur rather than on every keystroke
    // so we don't flash an error while the user is still typing.
    reValidateMode: "onBlur",
  });
  const warmWaf = useAwsWafWarmup();

  // Carry whatever the user has typed over to the forgot-password / register pages so they don't
  // have to retype it. Password is deliberately not forwarded (it would end up in the URL).
  const email = watch("email") || undefined;

  const [step, setStep] = useState<"email" | "password">("email");
  // Which methods this email's company allows, learned on the email step. Until then we don't know
  // the domain, so everything is offered.
  const [allowedMethods, setAllowedMethods] = useState<AllowedLoginMethods>(DEFAULT_LOGIN_METHODS);
  // Which enterprise IdP to start. Null when the company has none, and also when several verified
  // providers cover the domain — better-auth then picks one from the email.
  const [ssoProviderId, setSsoProviderId] = useState<string | null>(null);
  // A hand-off to an identity provider. Starts busy when a link asked for a provider, so the form
  // doesn't flash idle before it begins.
  const {
    status: redirectStatus,
    start: startRedirect,
    clearError: clearRedirectError,
  } = useProviderRedirect(Boolean(linkedProvider));
  const passwordRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (step === "password") {
      passwordRef.current?.focus();
    }
  }, [step]);

  const onAuthenticated = () => {
    // TODO(auth-migration): use a full navigation until the Rails app shell recognises the
    // Better Auth session.
    void navigate({ href: redirect ?? "/", reloadDocument: true });
  };

  // Signed in with a session. The sign-in response doesn't say whether the company mandates 2FA the
  // user hasn't set up — only the session does — so check it and go straight to setup if so, rather
  // than into the app (where Rails, lacking a cookie until setup, would bounce them back here).
  // Full loads so the next page reads the new session fresh.
  const enterApp = async () => {
    const session = await queryClient.fetchQuery({
      ...betterAuthSessionQueryOptions(),
      staleTime: 0,
    });
    if (session?.twoFactorSetupRequired) {
      await navigate({ to: "/auth/2fa-setup", search: { redirect }, reloadDocument: true });
      return;
    }
    onAuthenticated();
  };

  const {
    mutateAsync: checkMigrationStatus,
    isLoading: isCheckingEmail,
    isError: isCheckError,
    error: checkError,
  } = useMigrationStatus();

  const {
    mutate: signIn,
    isError: isSignInError,
    error: signInError,
    isLoading: isSigningIn,
  } = useSignIn({
    onSuccess: (result) => {
      if (result.token) {
        void enterApp();
      }

      // Enrolled user: a 2FA challenge is returned instead of a session (the challenge cookie is set),
      // so move to the 2FA page to collect the code. Below token check is deliberate to ensure the
      // session isn't issued until 2FA is completed
      if (result.twoFactorRedirect) {
        void navigate({ to: "/auth/2fa", search: { redirect } });
      }
    },
  });

  useLinkedProvider(linkedProvider, "login", (provider) =>
    startRedirect(() => signInWithProvider(provider, redirect, buildSignupContext(search))),
  );

  const startEnterpriseSso = (email: string, providerId: string | null) =>
    startRedirect(() => signInWithEnterpriseSSO(email, redirect, providerId ?? undefined));

  // Email step submit. We validate only the email here (not the whole form) — the password field
  // stays mounted for autofill but must not gate this step, or a returning user would be blocked by
  // a password error they can't even see.
  const onEmailContinue = async (event: FormEvent) => {
    event.preventDefault();
    if (!(await trigger("email"))) return;
    clearRedirectError();

    const email = getValues("email").trim();
    // A rejection is network / 5xx: already reported to Sentry in the hook and surfaced below via
    // isCheckError, so the user just stays on the email step to retry.
    const status = await checkMigrationStatus({ email }).catch(() => null);
    if (!status) return;

    setAllowedMethods(status.allowedMethods);
    setSsoProviderId(status.ssoProviderId);

    // One way in and it's a redirect: start it rather than render a list of one. Anything else
    // goes to the password step, which shows whatever the company allows.
    const soleMethod = soleRedirectMethod(status.allowedMethods);
    if (soleMethod === "sso") {
      await startEnterpriseSso(email, status.ssoProviderId);
      return;
    }
    if (soleMethod) {
      // Nothing was pressed, so send the GA4 event ExternalSignInButtons would have sent for a click.
      trackSocialSignIn("login", soleMethod);
      await startRedirect(() =>
        signInWithProvider(soleMethod, redirect, buildSignupContext(search), email),
      );
      return;
    }
    setStep("password");
  };

  const backToEmail = () => {
    // Clear the password (and any error on it) so a returning user starts the email step clean.
    setValue("password", "");
    clearErrors("password");
    setAllowedMethods(DEFAULT_LOGIN_METHODS);
    setSsoProviderId(null);
    clearRedirectError();
    setStep("email");
  };

  // `undefined` means no filter, which is all we can do before we know the email. After that, drop
  // the providers the company has switched off so we don't offer a button that leads to an error.
  const socialProviders: SocialProviderType[] | undefined =
    step === "password"
      ? (["google", "microsoft"] as const).filter((p) => allowedMethods[p])
      : undefined;
  const passwordAllowed = step === "email" || allowedMethods.password;
  // Only reached when SSO shares the page with another method — on its own the email step starts it.
  const ssoAllowed = step === "password" && allowedMethods.sso;
  // Whether the "use one of the options above" copy has anything to point at.
  const hasOtherMethods = Boolean(socialProviders?.length) || ssoAllowed;

  return (
    <Page>
      <Card>
        <StotlesLogo />
        <Text h1>Log in to Stotles</Text>

        {/* One form, both inputs always mounted — the inactive one is visually hidden. This lets a
            password manager fill email + password in a single pass, and keeps one shell for both
            steps. `step` drives which field is visible and which submit runs. */}
        <Form
          onSubmit={step === "email" ? onEmailContinue : handleSubmit((values) => signIn(values))}
          onFocusCapture={warmWaf}
        >
          <ExternalSignInButtons
            mode="login"
            providers={socialProviders}
            disabled={redirectStatus === "starting"}
            onSelect={(provider) =>
              startRedirect(() =>
                signInWithProvider(
                  provider,
                  redirect,
                  buildSignupContext(search),
                  getValues("email"),
                ),
              )
            }
            onEnterpriseSso={
              ssoAllowed
                ? () => startEnterpriseSso(getValues("email").trim(), ssoProviderId)
                : undefined
            }
          />

          {step === "password" && (
            <Text color="sysTextSecondary">
              Signing in as {watch("email")}.{" "}
              <Button buttonType="tertiary" type="button" onClick={backToEmail}>
                Use a different email
              </Button>
            </Text>
          )}

          <CollapsedField data-hidden={step !== "email" || undefined}>
            <Label htmlFor="email">Email</Label>
            <Input
              name="email"
              control={control}
              type="email"
              autoComplete="username"
              placeholder="name@email.com"
              tabIndex={step === "email" ? undefined : -1}
              aria-hidden={step !== "email"}
              rules={{
                required: { value: true, message: "Email is required" },
              }}
            />
            {errors.email?.message && (
              <Text color="sysDestructiveDefault">{errors.email.message}</Text>
            )}
          </CollapsedField>

          <CollapsedField data-hidden={step !== "password" || !passwordAllowed || undefined}>
            <Label htmlFor="password">Password</Label>
            <Input
              ref={passwordRef}
              name="password"
              control={control}
              type="password"
              autoComplete="current-password"
              placeholder="Enter your password"
              tabIndex={step === "password" && passwordAllowed ? undefined : -1}
              aria-hidden={step !== "password" || !passwordAllowed}
              // The field stays mounted for autofill, so skip validation when it isn't in use —
              // otherwise its "required" rule would block a form that has no password to give.
              rules={passwordAllowed ? passwordFieldRules : {}}
            />
            {errors.password?.message && (
              <Text color="sysDestructiveDefault">{errors.password.message}</Text>
            )}
          </CollapsedField>

          {step === "email" && isCheckError && (
            <Text color="sysDestructiveDefault">
              {betterAuthErrorText(checkError, "Something went wrong")}
            </Text>
          )}
          {redirectStatus === "failed" && (
            <Text color="sysDestructiveDefault">
              We couldn&apos;t reach your sign-in provider. Please try again.
            </Text>
          )}
          {/* TODO: Customise error messages */}
          {step === "password" && isSignInError && (
            <Text color="sysDestructiveDefault">
              {betterAuthErrorText(signInError, "Sign in failed")}
            </Text>
          )}

          {passwordAllowed ? (
            <Button
              buttonType="primary"
              type="submit"
              fullWidth
              loading={
                redirectStatus === "starting" || (step === "email" ? isCheckingEmail : isSigningIn)
              }
              disabled={step === "email" && !email}
            >
              {step === "email" ? "Continue" : "Log in"}
            </Button>
          ) : (
            <Alert type="warning">
              {hasOtherMethods
                ? "Your company doesn't allow signing in with a password — use one of the options above."
                : "Your company has turned off every sign-in method available here. Please contact your Stotles administrator."}
            </Alert>
          )}
        </Form>

        {passwordAllowed && (
          <Text color="sysTextSecondary">
            <Link to="/auth/forgot-password" search={{ email }}>
              Forgot your password?
            </Link>
          </Text>
        )}

        <Text color="sysTextSecondary">
          New to Stotles?{" "}
          {/* Merge form (not `{{ email }}`) so the login page's `redirect` is preserved onto the
              register page rather than replaced — register consumes it. */}
          <Link to="/auth/register" search={(prev) => ({ ...prev, email })}>
            Register here
          </Link>
        </Text>
      </Card>
    </Page>
  );
}
